import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import LockOutlined from "@mui/icons-material/LockOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  resetCollection,
  resetCompetition,
  resetTask,
  userLogin,
} from "../actions";
import simccLogo from "../assets/images/simcc-logo-128.png";
import { isEmpty, warningMessage } from "../functions/alert";
import { login } from "../functions/httpMethods";
import { showNotification } from "../functions/snackbar";
import "./SignIn.css";

const LOGIN_SLIDES = [
  {
    title: "Competition management, end to end.",
    description:
      "Configure contests, ingest answers, verify integrity, compute awards and issue certificates — for every country council partner, in one place.",
    type: "chips",
    items: ["SASMO", "SIMOC", "SMC", "AMO", "MMT", "IJIO", "IJCO"],
    stats: [
      ["950k+", "Students"],
      ["62k", "Schools"],
      ["65", "Countries"],
      ["45", "Partners"],
    ],
  },
  {
    title: "Six olympiads. One platform.",
    description:
      "Every contest SIMCC runs — configured, marked and certified through the same pipeline.",
    type: "domains",
    items: [
      ["∑", "Mathematics", "SASMO · SIMOC · AMO"],
      ["⚗", "Science", "Olympiad"],
      ["</>", "Informatics", "IJIO"],
      ["◇", "Cybersecurity", "IJCO"],
      ["▤", "Accounting", "Olympiad"],
      ["✦", "Art & UX", "Design contests"],
    ],
    stats: [
      ["950k+", "Students"],
      ["62k", "Schools"],
      ["65", "Countries"],
    ],
  },
  {
    title: "Run one contest across sixty-five countries.",
    description:
      "Marking groups keep results fair between regions. Every partner works in their own scope, on the same competition.",
    type: "regions",
    items: [
      "Singapore",
      "Indonesia",
      "Malaysia",
      "Vietnam",
      "China",
      "India",
      "Azerbaijan",
      "Egypt",
      "Saudi Arabia",
      "+ 56 more",
    ],
    stats: [
      ["65", "Countries"],
      ["45", "Council partners"],
      ["62k", "Schools"],
    ],
  },
  {
    title: "Every award you issue starts here.",
    description:
      "Marking, moderation and certificates for contests that lead to medals, prizes and scholarship pathways.",
    type: "awards",
    items: [
      ["1", "Perfect Score", "Cash prize · certificate · medal"],
      ["2", "Gold · Silver · Bronze", "Ranked per marking group"],
      ["3", "Honourable Mention", "Certificate of participation"],
    ],
    stats: [
      ["950k+", "Students"],
      ["65", "Countries"],
      ["45", "Partners"],
    ],
  },
];

function SlideDetails({ slide }) {
  if (slide.type === "domains") {
    return (
      <div className="signin-domain-grid">
        {slide.items.map(([symbol, title, subtitle]) => (
          <div className="signin-domain" key={title}>
            <span className="signin-domain__icon" aria-hidden="true">
              {symbol}
            </span>
            <strong>{title}</strong>
            <small>{subtitle}</small>
          </div>
        ))}
      </div>
    );
  }

  if (slide.type === "awards") {
    return (
      <div className="signin-awards">
        {slide.items.map(([rank, title, subtitle]) => (
          <div className="signin-award" key={title}>
            <span className={`signin-medal signin-medal--${rank}`}>{rank}</span>
            <span>
              <strong>{title}</strong>
              <small>{subtitle}</small>
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="signin-chip-list">
      {slide.items.map((item) => (
        <span className="signin-chip" key={item}>
          {item}
        </span>
      ))}
    </div>
  );
}

SlideDetails.propTypes = {
  slide: PropTypes.shape({
    type: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }).isRequired,
};

function BrandPanel({ paused, setPaused }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReduceMotion(mediaQuery.matches);
    updatePreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updatePreference);
      return () => mediaQuery.removeEventListener("change", updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  useEffect(() => {
    if (paused || reduceMotion) return undefined;
    const timer = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % LOGIN_SLIDES.length),
      6500
    );
    return () => window.clearInterval(timer);
  }, [paused, reduceMotion]);

  useEffect(() => {
    const changeSlideWithKeyboard = (event) => {
      if (event.key === "ArrowLeft") {
        setActiveSlide(
          (current) =>
            (current - 1 + LOGIN_SLIDES.length) % LOGIN_SLIDES.length
        );
      }
      if (event.key === "ArrowRight") {
        setActiveSlide((current) => (current + 1) % LOGIN_SLIDES.length);
      }
    };
    document.addEventListener("keydown", changeSlideWithKeyboard);
    return () => document.removeEventListener("keydown", changeSlideWithKeyboard);
  }, []);

  const slide = LOGIN_SLIDES[activeSlide];
  const step = (direction) => {
    setActiveSlide(
      (current) =>
        (current + direction + LOGIN_SLIDES.length) % LOGIN_SLIDES.length
    );
  };

  return (
    <aside
      className="signin-brand-panel"
      aria-label="About SIMCC"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="signin-brand-effects" aria-hidden="true">
        <span className="signin-brand-blob signin-brand-blob--blue" />
        <span className="signin-brand-blob signin-brand-blob--gold" />
        <span className="signin-brand-grid" />
        {slide.type === "regions" && <span className="signin-globe" />}
      </div>

      <div className="signin-brand-lockup">
        <img src={simccLogo} alt="SIMCC" />
        <span>
          <strong>SIMCC</strong>
          <small>Competition CMS</small>
        </span>
      </div>

      <section className="signin-slide" key={activeSlide}>
        <div className="signin-gold-rule" />
        <h1>{slide.title}</h1>
        <p>{slide.description}</p>
        <SlideDetails slide={slide} />
      </section>

      <div className="signin-stats">
        {slide.stats.map(([value, label]) => (
          <div key={label}>
            <strong>{value}</strong>
            <small>{label}</small>
          </div>
        ))}
      </div>

      <div className="signin-carousel-controls">
        <div className="signin-carousel-dots" aria-label="Choose a slide">
          {LOGIN_SLIDES.map((item, index) => (
            <button
              type="button"
              className={index === activeSlide ? "is-active" : ""}
              aria-label={`Show slide ${index + 1}: ${item.title}`}
              aria-current={index === activeSlide ? "true" : undefined}
              onClick={() => setActiveSlide(index)}
              key={item.title}
            />
          ))}
        </div>
        <div className="signin-carousel-arrows">
          <button type="button" onClick={() => step(-1)} aria-label="Previous slide">
            <ChevronLeft fontSize="small" />
          </button>
          <button type="button" onClick={() => step(1)} aria-label="Next slide">
            <ChevronRight fontSize="small" />
          </button>
        </div>
      </div>
    </aside>
  );
}

BrandPanel.propTypes = {
  paused: PropTypes.bool.isRequired,
  setPaused: PropTypes.func.isRequired,
};

export default function SignIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [wrongLogin, setWrongLogin] = useState(false);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = "Sign In";
  }, []);

  // Authentication and backend communication intentionally remain unchanged.
  const onSubmit = () => {
    if (isEmpty([username, password])) {
      return warningMessage([
        { name: "Username", state: username },
        { name: "Password", state: password },
      ]);
    }
    login({ username, password })
      .then((d) => {
        if (d.status !== 200) {
          showNotification("error", d.message, enqueueSnackbar, closeSnackbar);
          setWrongLogin(true);
          return;
        }
        console.log("d");
        console.log(d);
        dispatch(
          userLogin(
            true,
            username,
            d.role_id,
            d.user_id,
            d.country_id,
            d.school_id,
            d.parent_id,
            d.private,
            d.token,
            d?.organization,
            d?.country,
            d?.school
          )
        );
        dispatch(resetTask());
        dispatch(resetCollection());
        dispatch(resetCompetition());
      })
      .catch((e) => console.log(e));
  };

  const handleUsernameChange = (event) => setUsername(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const onPressEnter = (event) => event.key === "Enter" && onSubmit();

  return (
    <main className="signin-page">
      <BrandPanel paused={carouselPaused} setPaused={setCarouselPaused} />

      <section
        className="signin-form-panel"
        aria-labelledby="signin-title"
        onFocusCapture={() => setCarouselPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setCarouselPaused(false);
          }
        }}
      >
        <div className="signin-form-card">
          <h2 id="signin-title">Sign in</h2>
          <p className="signin-form-subtitle">
            Welcome back. Enter your details to continue.
          </p>

          <div className="signin-field">
            <label htmlFor="signin-username">Username or email</label>
            <div className="signin-input-wrap">
              <PersonOutline aria-hidden="true" />
              <input
                id="signin-username"
                name="username"
                type="text"
                autoComplete="username"
                placeholder="you@simcc.org"
                value={username}
                onChange={handleUsernameChange}
                onKeyDown={onPressEnter}
              />
            </div>
          </div>

          <div className="signin-field">
            <label htmlFor="signin-password">Password</label>
            <div className="signin-input-wrap signin-input-wrap--password">
              <LockOutlined aria-hidden="true" />
              <input
                id="signin-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={onPressEnter}
              />
              <button
                type="button"
                className="signin-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </button>
            </div>
          </div>

          <div className="signin-feedback" aria-live="polite">
            {wrongLogin && <span role="alert">Invalid credentials</span>}
          </div>

          <button type="button" className="signin-submit" onClick={onSubmit}>
            Sign in
          </button>

          <p className="signin-help">
            Trouble signing in? Contact your country partner administrator
            <br />
            or <a href="mailto:admin@simcc.org">admin@simcc.org</a>
          </p>
        </div>
      </section>
    </main>
  );
}
